import { createMemo, For, Show } from "solid-js";
import { useI18n } from "@/api/i18n";
import { isJobCategory } from "@/domains/job-offer/create/constants";
import { cn } from "@/libs/cn";
import { categoryIconClass } from "../../constants/category-icons";
import { MAX_CATEGORIES } from "../../selector/sector-draft";
import type { SectorDraft, SlotIndex } from "../../selector/types";
import {
  type CategoryOption,
  categoryOptions,
} from "../../store/taxonomy-store";
import {
  COLOR_TRANSITION,
  FOCUS_RING,
  SlotBadge,
  SuggestedMark,
} from "./primitives";

const I18N = "taxonomy.sector" as const;

/**
 * A sector as a tile. Chosen it becomes the primary or the secondary; Jev's
 * proposal is a dashed tile and touching it is accepting it.
 *
 * The DOM order is the order of the accessible name: the label, then the badge,
 * then the note (AC27). The icon and the badge sit on the first visual line
 * through `order-*`; if the badge does not fit next to the icon it wraps below
 * it, inside the tile (AC32).
 */
const CategoryTile = (props: {
  draft: SectorDraft;
  option: CategoryOption;
  onPick?: (index: SlotIndex) => void;
}) => {
  const { t } = useI18n();

  const index = createMemo<SlotIndex | null>(() => {
    if (props.draft.slots[0]?.category === props.option.value) return 0;
    if (props.draft.slots[1]?.category === props.option.value) return 1;
    return null;
  });
  const chosen = () => index() !== null;
  const proposal = () =>
    props.draft.visibleProposal()?.category === props.option.value;
  /** Both sectors are taken and this is not one of them (AC1). */
  const full = () =>
    !chosen() && props.draft.chosenCategories().length >= MAX_CATEGORIES;
  /** The saved primary in the profile: it changes, it is never removed alone (AC4). */
  const locked = () => {
    const i = index();
    return i !== null && !props.draft.canRemove(i);
  };

  const toggle = () => {
    const i = index();
    if (i !== null) {
      if (props.draft.canRemove(i)) props.draft.removeCategory(i);
      return;
    }
    const free = props.draft.freeIndex();
    const category = props.option.value;
    if (free === null || !isJobCategory(category)) return;
    props.draft.setCategory(free, category);
    props.onPick?.(free);
  };

  return (
    <button
      type="button"
      aria-pressed={chosen()}
      disabled={full() || locked()}
      onClick={toggle}
      class={cn(
        "flex h-full min-h-24 w-full flex-wrap content-start items-start gap-x-2 gap-y-3 rounded-lg border p-3 text-left disabled:cursor-not-allowed @md:p-4",
        COLOR_TRANSITION,
        FOCUS_RING,
        chosen()
          ? "border-primary bg-primary/10"
          : proposal()
            ? "border-dashed border-ring bg-primary/5 hover:bg-primary/10"
            : "border-border bg-white hover:border-foreground/30 hover:bg-muted/40",
        full() && "opacity-50",
      )}
    >
      <i
        class={cn(
          "ph order-1 text-2xl leading-none",
          categoryIconClass(props.option.value),
          chosen() ? "text-primary" : "text-foreground/70",
        )}
        aria-hidden="true"
      />
      <span class="order-3 w-full min-w-0 break-words text-sm font-semibold leading-tight">
        {props.option.label}
      </span>
      <Show when={index() !== null}>
        {" "}
        <SlotBadge index={index() as SlotIndex} class="order-2 ml-auto" />
      </Show>
      <Show when={proposal()}>
        {" "}
        <span class="order-4 -mt-2 flex w-full items-start gap-1 text-xs text-ring">
          <SuggestedMark class="mt-0.5" />
          <span>{t(`${I18N}.fitsProfile`)}</span>
        </span>
      </Show>
      <Show when={locked()}>
        {" "}
        <span class="order-4 -mt-2 w-full text-xs text-foreground/70">
          {t(`${I18N}.chooseFirst`)}
        </span>
      </Show>
    </button>
  );
};

/**
 * The eight sector tiles in the order of the endpoint: 2 columns, 3 from
 * `@md`, 4 from `@2xl` (AC33). Each tile is `h-full` so a row has one height.
 */
export const CategoryGrid = (props: {
  draft: SectorDraft;
  labelledBy: string;
  onPick?: (index: SlotIndex) => void;
}) => (
  <ul
    class="grid grid-cols-2 gap-3 @md:grid-cols-3 @2xl:grid-cols-4"
    aria-labelledby={props.labelledBy}
  >
    <For each={categoryOptions()}>
      {(option) => (
        <li>
          <CategoryTile
            draft={props.draft}
            option={option}
            onPick={props.onPick}
          />
        </li>
      )}
    </For>
  </ul>
);
